import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Bell, BellOff, Settings, MessageCircle } from 'lucide-react';
import { adminService } from '../../services/api';
import {
  TreatmentType,
  CreateTreatmentTypeData,
  ConfigRappelTraitement,
  CreateConfigRappelData,
} from '../../types';
import DataTable from '../common/DataTable';
import LoadingSpinner from '../common/LoadingSpinner';
import TreatmentTypeForm from './TreatmentTypeForm';

interface TreatmentTypeManagementProps {
  isVisible: boolean;
}

const PRIORITE_LABELS: Record<string, string> = {
  low: 'Basse',
  normal: 'Normale',
  high: 'Haute',
};

// ── Modal configuration rappel ────────────────────────────────────────────────

interface RappelModalProps {
  treatmentType: TreatmentType;
  onClose: () => void;
}

const RappelConfigModal: React.FC<RappelModalProps> = ({ treatmentType, onClose }) => {
  const queryClient = useQueryClient();
  const existing = treatmentType.config_rappel;

  const [form, setForm] = useState<CreateConfigRappelData>({
    treatment_type: treatmentType.id,
    actif: existing?.actif ?? true,
    delai_mois: existing?.delai_mois ?? 6,
    delai_jours: existing?.delai_jours ?? 0,
    duree_rdv_minutes: existing?.duree_rdv_minutes ?? 30,
    motif_template: existing?.motif_template ?? 'Contrôle - {traitement}',
    priorite: existing?.priorite ?? 'normal',
    envoyer_notif_creation: existing?.envoyer_notif_creation ?? true,
  });

  const saveMutation = useMutation({
    mutationFn: (data: CreateConfigRappelData) =>
      existing
        ? adminService.updateConfigRappel(existing.id, data)
        : adminService.createConfigRappel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-treatment-types'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminService.deleteConfigRappel(existing!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-treatment-types'] });
      onClose();
    },
  });

  const handleChange = (field: keyof CreateConfigRappelData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Rappel RDV automatique</h3>
              <p className="text-sm text-gray-500 mt-0.5">{treatmentType.name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
          </div>

          {/* Actif toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only"
                checked={form.actif}
                onChange={e => handleChange('actif', e.target.checked)}
              />
              <div className={`w-10 h-6 rounded-full transition-colors ${form.actif ? 'bg-blue-600' : 'bg-gray-300'}`} />
              <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.actif ? 'translate-x-4' : ''}`} />
            </div>
            <span className="text-sm font-medium text-gray-700">
              {form.actif ? 'Rappel activé' : 'Rappel désactivé'}
            </span>
          </label>

          {form.actif && (
            <div className="space-y-4">
              {/* Délai */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Délai avant le contrôle</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Mois</label>
                    <input
                      type="number"
                      min={0}
                      max={60}
                      value={form.delai_mois}
                      onChange={e => handleChange('delai_mois', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Jours additionnels</label>
                    <input
                      type="number"
                      min={0}
                      max={30}
                      value={form.delai_jours}
                      onChange={e => handleChange('delai_jours', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  RDV planifié {form.delai_mois > 0 ? `${form.delai_mois} mois` : ''}{form.delai_jours > 0 ? ` ${form.delai_jours} jours` : ''} après le paiement
                </p>
              </div>

              {/* Durée */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Durée du RDV de contrôle (minutes)</label>
                <select
                  value={form.duree_rdv_minutes}
                  onChange={e => handleChange('duree_rdv_minutes', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {[15, 20, 30, 45, 60, 90].map(m => (
                    <option key={m} value={m}>{m} min</option>
                  ))}
                </select>
              </div>

              {/* Motif */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modèle du motif
                </label>
                <input
                  type="text"
                  value={form.motif_template}
                  onChange={e => handleChange('motif_template', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Contrôle - {traitement}"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Aperçu : <span className="font-medium text-gray-600">
                    {form.motif_template.replace('{traitement}', treatmentType.name)}
                  </span>
                </p>
              </div>

              {/* Priorité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priorité du RDV</label>
                <select
                  value={form.priorite}
                  onChange={e => handleChange('priorite', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="low">Basse</option>
                  <option value="normal">Normale</option>
                  <option value="high">Haute</option>
                </select>
              </div>

              {/* Notification WhatsApp à la création */}
              <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="relative mt-0.5 shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={form.envoyer_notif_creation}
                      onChange={e => handleChange('envoyer_notif_creation', e.target.checked)}
                    />
                    <div className={`w-9 h-5 rounded-full transition-colors ${form.envoyer_notif_creation ? 'bg-green-600' : 'bg-gray-300'}`} />
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.envoyer_notif_creation ? 'translate-x-4' : ''}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <MessageCircle className="h-4 w-4 text-green-700" />
                      <span className="text-sm font-medium text-green-800">
                        Notifier le patient à la création
                      </span>
                    </div>
                    <p className="text-xs text-green-700 mt-0.5">
                      Envoie un message WhatsApp/email dès que le RDV de contrôle est planifié,
                      en utilisant le template <em>Suivi de traitement</em> du module Communications.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div>
              {existing && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Supprimer la configuration de rappel ?'))
                      deleteMutation.mutate();
                  }}
                  disabled={deleteMutation.isPending}
                  className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                >
                  Supprimer la config
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => saveMutation.mutate(form)}
                disabled={saveMutation.isPending}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

const TreatmentTypeManagement: React.FC<TreatmentTypeManagementProps> = ({ isVisible }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingTreatmentType, setEditingTreatmentType] = useState<TreatmentType | null>(null);
  const [rappelModalFor, setRappelModalFor] = useState<TreatmentType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const queryClient = useQueryClient();

  const { data: treatmentTypesData, isLoading } = useQuery<TreatmentType[]>({
    queryKey: ['admin-treatment-types', searchTerm],
    keepPreviousData: true,
    queryFn: () =>
      adminService.getTreatmentTypesAdmin({
        search: searchTerm,
        page_size: 1000,
        ordering: 'name',
      }).then(res => {
        // L'API retourne un tableau brut (pagination_class = None)
        const raw = res.data as any;
        return Array.isArray(raw) ? raw : (raw.results ?? []);
      }),
    enabled: isVisible,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateTreatmentTypeData) => adminService.createTreatmentType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-treatment-types'] });
      setShowForm(false);
      setEditingTreatmentType(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateTreatmentTypeData> }) =>
      adminService.updateTreatmentType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-treatment-types'] });
      setShowForm(false);
      setEditingTreatmentType(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteTreatmentType(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-treatment-types'] }),
  });

  const handleSubmit = (data: CreateTreatmentTypeData) => {
    if (editingTreatmentType) {
      updateMutation.mutate({ id: editingTreatmentType.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const columns = [
    { key: 'name', label: 'Nom' },
    {
      key: 'description',
      label: 'Description',
      render: (_: any, t: TreatmentType) => t.description || '-',
    },
    {
      key: 'base_price',
      label: 'Prix de base',
      render: (_: any, t: TreatmentType) => t.non_facturable
        ? <span className="son-chip son-chip-gray">Non facturable</span>
        : `${Number(t.base_price).toLocaleString()} GNF`,
    },
    { key: 'duration_minutes', label: 'Durée (min)' },
    {
      key: 'rappel',
      label: 'Rappel RDV',
      sortable: false,
      render: (_: any, t: TreatmentType) => {
        const cfg = t.config_rappel;
        if (!cfg || !cfg.actif) {
          return (
            <button
              onClick={() => setRappelModalFor(t)}
              title="Configurer le rappel automatique"
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors"
            >
              <BellOff className="h-4 w-4" />
              <span>Non configuré</span>
            </button>
          );
        }
        return (
          <button
            onClick={() => setRappelModalFor(t)}
            title="Modifier la configuration du rappel"
            className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 hover:bg-green-100 transition-colors"
          >
            <Bell className="h-3.5 w-3.5" />
            <span>{cfg.delai_mois}m{cfg.delai_jours > 0 ? `+${cfg.delai_jours}j` : ''}</span>
            <span className="text-green-600">· {PRIORITE_LABELS[cfg.priorite]}</span>
          </button>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_: any, t: TreatmentType) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setRappelModalFor(t)}
            className="text-purple-600 hover:text-purple-900"
            title="Configurer le rappel RDV"
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            onClick={() => { setEditingTreatmentType(t); setShowForm(true); }}
            className="text-blue-600 hover:text-blue-900"
            title="Modifier"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (window.confirm(`Supprimer le type de traitement "${t.name}" ?`))
                deleteMutation.mutate(t.id);
            }}
            className="text-red-600 hover:text-red-900"
            title="Supprimer"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (!isVisible) return null;
  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Types de traitement</h2>
          <p className="mt-1 text-sm text-gray-500">
            Gérez le catalogue des traitements et configurez les rappels RDV automatiques
          </p>
        </div>
        <button
          onClick={() => { setEditingTreatmentType(null); setShowForm(true); }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nouveau traitement
        </button>
      </div>

      {/* Légende rappels */}
      <div className="flex items-center gap-4 text-xs text-gray-500 bg-blue-50 rounded-lg px-4 py-3">
        <Bell className="h-4 w-4 text-blue-500 shrink-0" />
        <span>
          Cliquez sur <strong>l'icône engrenage</strong> ou sur le badge de rappel pour configurer
          un rendez-vous de contrôle automatique. Il sera planifié dans le planning dès le paiement
          de la consultation correspondante.
        </span>
      </div>

      {/* Recherche */}
      <div className="flex items-center justify-between gap-4">
        <input
          type="text"
          placeholder="Rechercher un type de traitement..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900"
        />
        {treatmentTypesData && (
          <div className="text-sm text-gray-600 whitespace-nowrap">
            {treatmentTypesData.length} traitement(s)
          </div>
        )}
      </div>

      <DataTable
        data={treatmentTypesData || []}
        columns={columns}
        loading={isLoading}
      />

      {/* Formulaire création/édition */}
      {showForm && (
        <TreatmentTypeForm
          treatmentType={editingTreatmentType}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); setEditingTreatmentType(null); }}
          loading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Modal configuration rappel */}
      {rappelModalFor && (
        <RappelConfigModal
          treatmentType={rappelModalFor}
          onClose={() => setRappelModalFor(null)}
        />
      )}
    </div>
  );
};

export default TreatmentTypeManagement;
